import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useGeneratedImagesStore } from "@/contexts/GeneratedImagesContext";
import { captureEvent } from "@/lib/analyticsQueue";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { COMMUNITY_PROMPT_SEEN_KEY } from "./constants";

function seenKey(userId: string): string {
  return `${COMMUNITY_PROMPT_SEEN_KEY}_${userId}`;
}

/**
 * Opens the community modal exactly once, right after the user's first
 * successful generation. Flow-agnostic: it watches the shared
 * GeneratedImagesContext (fed by realtime from every flow) rather than hooking
 * into any single generation hook.
 *
 * A historical baseline is captured once the initial load settles, so a user
 * with prior generations never sees it on load — only a *fresh* completion that
 * lifts the count above that baseline triggers it. A per-user localStorage flag
 * makes it once-ever, surviving reloads (matching MarketingConsentModal).
 */
export function useFirstGenerationCommunityModal() {
  const { user } = useAuth();
  const { byId, loading } = useGeneratedImagesStore();
  const [isOpen, setIsOpen] = useState(false);

  const baselineRef = useRef<number | null>(null);
  const userIdRef = useRef<string | null>(null);

  const completedCount = useMemo(
    () => Object.values(byId).filter((img) => img.status === "completed").length,
    [byId],
  );

  // Reset the baseline when the signed-in user changes (logout/login) so we
  // never compare one account's count against another's.
  useEffect(() => {
    if (user?.id !== userIdRef.current) {
      userIdRef.current = user?.id ?? null;
      baselineRef.current = null;
    }
  }, [user?.id]);

  // Capture the historical baseline once the first load finishes.
  useEffect(() => {
    if (loading) return;
    if (baselineRef.current === null) baselineRef.current = completedCount;
  }, [loading, completedCount]);

  useEffect(() => {
    if (!user || baselineRef.current === null || isOpen) return;
    if (completedCount <= baselineRef.current) return;

    try {
      if (localStorage.getItem(seenKey(user.id))) return;
      localStorage.setItem(seenKey(user.id), new Date().toISOString());
    } catch {
      // localStorage unavailable (private mode) — skip the prompt silently.
      return;
    }

    setIsOpen(true);
    captureEvent(ANALYTICS_EVENTS.COMMUNITY_MODAL_SHOWN, {
      source: "first_generation",
    });
  }, [completedCount, user, isOpen]);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    captureEvent(ANALYTICS_EVENTS.COMMUNITY_MODAL_DISMISSED, {
      source: "first_generation",
    });
  }, []);

  return { isOpen, dismiss };
}
